import { useCallback, useEffect, useRef, useState } from "react";
import {
  AssistantHttpError,
  assistantApi,
  assistantStream,
  isUpgradeError,
  type PagePayload,
} from "./assistantApi";
import type {
  AssistantAction,
  AssistantMode,
  ChatItem,
  DisplayItem,
  StreamEvent,
  ThreadSummary,
} from "./types";

const MODE_KEY = "kavoa.assistant.mode";

function readMode(): AssistantMode {
  try {
    return localStorage.getItem(MODE_KEY) === "auto" ? "auto" : "manual";
  } catch {
    return "manual";
  }
}

let idSeq = 0;
const nextId = () => `i${Date.now().toString(36)}${(idSeq++).toString(36)}`;

function toChatItems(items: DisplayItem[], pending: AssistantAction[]): ChatItem[] {
  const pendingById = new Map(pending.map((a) => [a._id, a]));
  const seen = new Set<string>();
  const out: ChatItem[] = [];
  for (const it of items) {
    if (it.role === "user" && it.text) out.push({ id: nextId(), kind: "user", text: it.text });
    else if (it.role === "assistant" && it.text) out.push({ id: nextId(), kind: "assistant", text: it.text });
    else if (it.role === "step") {
      const last = out[out.length - 1];
      const label = it.label || it.text || "Looked something up";
      if (last && last.kind === "steps") last.labels.push(label);
      else out.push({ id: nextId(), kind: "steps", labels: [label], done: true });
    } else if (it.role === "action" && it.action?._id) {
      const a = pendingById.get(it.action._id) || it.action;
      seen.add(a._id);
      out.push({ id: nextId(), kind: "action", action: a });
    }
  }
  for (const a of pending) {
    if (!seen.has(a._id)) out.push({ id: nextId(), kind: "action", action: a });
  }
  return out;
}

export interface AssistantChat {
  mode: AssistantMode;
  setMode: (m: AssistantMode) => void;
  threadId: string | null;
  threadTitle: string;
  items: ChatItem[];
  streaming: boolean;
  loadingThread: boolean;
  upgradeRequired: boolean;
  markUpgradeRequired: () => void;
  threads: ThreadSummary[];
  threadsLoaded: boolean;
  usage: { used: number; limit: number | null } | null;
  refreshThreads: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  send: (text: string, page: PagePayload) => Promise<void>;
  approve: (actionId: string, edits?: Record<string, string>) => Promise<void>;
  skip: (actionId: string) => Promise<void>;
  stop: () => void;
  newChat: () => void;
  openThread: (id: string) => Promise<void>;
  deleteThread: (id: string) => Promise<void>;
  /** Unsent composer text, kept here so it survives closing the panel. */
  draft: string;
  setDraft: (v: string) => void;
}

export function useAssistantChat(): AssistantChat {
  const [mode, setModeState] = useState<AssistantMode>(readMode);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [threadTitle, setThreadTitle] = useState("");
  const [items, setItems] = useState<ChatItem[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [threadsLoaded, setThreadsLoaded] = useState(false);
  const [usage, setUsage] = useState<{ used: number; limit: number | null } | null>(null);
  const [draft, setDraft] = useState("");
  const abortRef = useRef<AbortController | null>(null);
  const threadIdRef = useRef<string | null>(null);
  threadIdRef.current = threadId;

  const silentAborts = useRef(new WeakSet<AbortController>());
  /** Abort the in-flight stream without a "Stopped." notice (thread switch, new chat). */
  const abortSilently = useCallback(() => {
    const c = abortRef.current;
    if (c) {
      silentAborts.current.add(c);
      c.abort();
    }
  }, []);

  useEffect(() => () => abortSilently(), [abortSilently]);

  const markUpgradeRequired = useCallback(() => setUpgradeRequired(true), []);

  const refreshThreads = useCallback(async () => {
    try {
      const list = await assistantApi.listThreads();
      setThreads(list);
      setUpgradeRequired(false);
    } catch (err) {
      if (isUpgradeError(err)) setUpgradeRequired(true);
    } finally {
      setThreadsLoaded(true);
    }
  }, []);

  const refreshUsage = useCallback(async () => {
    try {
      setUsage(await assistantApi.getUsage());
    } catch {
      setUsage(null);
    }
  }, []);

  const setMode = useCallback((m: AssistantMode) => {
    setModeState(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {}
    const id = threadIdRef.current;
    if (id) assistantApi.setThreadMode(id, m).catch(() => {});
  }, []);

  const closeSteps = (prev: ChatItem[]) =>
    prev.map((it) => (it.kind === "steps" && !it.done ? { ...it, done: true } : it));

  const applyEvent = useCallback((ev: StreamEvent) => {
    if (!ev || typeof ev !== "object") return;
    switch (ev.type) {
      case "step":
        setItems((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.kind === "steps" && !last.done) {
            return [...prev.slice(0, -1), { ...last, labels: [...last.labels, ev.label] }];
          }
          return [...prev, { id: nextId(), kind: "steps", labels: [ev.label], done: false }];
        });
        break;
      case "action":
        if (!ev.action?._id) break;
        setItems((prev) => {
          const idx = prev.findIndex((it) => it.kind === "action" && it.action._id === ev.action._id);
          if (idx >= 0) {
            const copy = prev.slice();
            copy[idx] = { ...(copy[idx] as any), action: ev.action, busy: false };
            return copy;
          }
          return [...prev, { id: nextId(), kind: "action", action: ev.action }];
        });
        break;
      case "message":
        if (ev.text) setItems((prev) => [...prev, { id: nextId(), kind: "assistant", text: ev.text }]);
        break;
      case "done":
        setItems(closeSteps);
        if (ev.threadTitle) setThreadTitle(ev.threadTitle);
        break;
      case "error":
        if (ev.code === "upgrade_required") setUpgradeRequired(true);
        setItems((prev) => [
          ...closeSteps(prev),
          { id: nextId(), kind: "error", text: ev.message || "Something went wrong." },
        ]);
        break;
    }
  }, []);

  /** Pull the latest action statuses from the server into the visible cards. */
  const syncActions = useCallback(async (id: string) => {
    try {
      const { thread, pendingActions } = await assistantApi.getThread(id);
      const fresh = new Map<string, AssistantAction>();
      thread.items.forEach((it) => it.action?._id && fresh.set(it.action._id, it.action));
      pendingActions.forEach((a) => fresh.set(a._id, a));
      setItems((prev) =>
        prev.map((it) =>
          it.kind === "action" && fresh.has(it.action._id)
            ? { ...it, action: fresh.get(it.action._id)!, busy: false }
            : it.kind === "action"
            ? { ...it, busy: false }
            : it
        )
      );
    } catch {
      setItems((prev) => prev.map((it) => (it.kind === "action" ? { ...it, busy: false } : it)));
    }
  }, []);

  const runStream = useCallback(
    async (fn: (signal: AbortSignal) => Promise<void>) => {
      abortSilently();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setStreaming(true);
      try {
        await fn(ctrl.signal);
      } catch (err: any) {
        if (err?.name === "AbortError" || ctrl.signal.aborted) {
          if (!silentAborts.current.has(ctrl)) setItems((prev) => [...closeSteps(prev), { id: nextId(), kind: "notice", text: "Stopped." }]);
        } else if (err instanceof AssistantHttpError && err.code === "upgrade_required") {
          setUpgradeRequired(true);
        } else {
          setItems((prev) => [
            ...closeSteps(prev),
            {
              id: nextId(),
              kind: "error",
              text: err?.message || "Something went wrong. Please try again.",
            },
          ]);
        }
      } finally {
        if (abortRef.current === ctrl) {
          abortRef.current = null;
          setItems(closeSteps);
          setStreaming(false);
        }
      }
    },
    [abortSilently]
  );

  const send = useCallback(
    async (text: string, page: PagePayload) => {
      const content = text.trim();
      if (!content) return;
      // The server expires any pending action in this thread when a new message arrives
      setItems((prev) => [
        ...prev.map((it) =>
          it.kind === "action" && it.action.status === "pending"
            ? { ...it, busy: false, action: { ...it.action, status: "expired" as const, error: "Superseded by a new message." } }
            : it
        ),
        { id: nextId(), kind: "user", text: content },
      ]);
      let id = threadIdRef.current;
      if (!id) {
        try {
          const t = await assistantApi.createThread(mode);
          id = t?._id || null;
          if (!id) throw new Error("no id");
          setThreadId(id);
          threadIdRef.current = id;
        } catch (err) {
          if (isUpgradeError(err)) {
            setUpgradeRequired(true);
          } else {
            setItems((prev) => [
              ...prev,
              { id: nextId(), kind: "error", text: "Could not start a conversation. Please try again." },
            ]);
          }
          return;
        }
      }
      const tid = id;
      await runStream((signal) =>
        assistantStream.sendMessage(tid, { content, page, mode }, applyEvent, signal)
      );
      refreshThreads();
      refreshUsage();
    },
    [mode, runStream, applyEvent, refreshThreads, refreshUsage]
  );

  const decide = useCallback(
    async (actionId: string, kind: "approve" | "skip", edits?: Record<string, string>) => {
      setItems((prev) =>
        prev.map((it) => (it.kind === "action" && it.action._id === actionId ? { ...it, busy: true } : it))
      );
      await runStream((signal) =>
        kind === "approve"
          ? assistantStream.approve(actionId, edits, applyEvent, signal)
          : assistantStream.skip(actionId, applyEvent, signal)
      );
      const id = threadIdRef.current;
      if (id) await syncActions(id);
      else setItems((prev) => prev.map((it) => (it.kind === "action" ? { ...it, busy: false } : it)));
      refreshThreads();
    },
    [runStream, applyEvent, syncActions, refreshThreads]
  );

  const approve = useCallback(
    (actionId: string, edits?: Record<string, string>) => decide(actionId, "approve", edits),
    [decide]
  );
  const skip = useCallback((actionId: string) => decide(actionId, "skip"), [decide]);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const newChat = useCallback(() => {
    abortSilently();
    setThreadId(null);
    threadIdRef.current = null;
    setThreadTitle("");
    setItems([]);
  }, [abortSilently]);

  const openThread = useCallback(async (id: string) => {
    abortSilently();
    setLoadingThread(true);
    setThreadId(id);
    threadIdRef.current = id;
    setItems([]);
    try {
      const { thread, pendingActions } = await assistantApi.getThread(id);
      if (threadIdRef.current !== id) return;
      setThreadTitle(thread.title || "");
      setItems(toChatItems(thread.items, pendingActions));
    } catch (err) {
      if (isUpgradeError(err)) setUpgradeRequired(true);
      else setItems([{ id: nextId(), kind: "error", text: "Could not load this conversation." }]);
    } finally {
      setLoadingThread(false);
    }
  }, [abortSilently]);

  const deleteThread = useCallback(
    async (id: string) => {
      setThreads((prev) => prev.filter((t) => t._id !== id));
      if (threadIdRef.current === id) newChat();
      try {
        await assistantApi.deleteThread(id);
      } catch {
        refreshThreads();
      }
    },
    [newChat, refreshThreads]
  );

  return {
    mode,
    setMode,
    threadId,
    threadTitle,
    items,
    streaming,
    loadingThread,
    upgradeRequired,
    markUpgradeRequired,
    threads,
    threadsLoaded,
    usage,
    refreshThreads,
    refreshUsage,
    send,
    approve,
    skip,
    stop,
    newChat,
    openThread,
    deleteThread,
    draft,
    setDraft,
  };
}
